import type { WrittenPage } from '../sections'

/**
 * The Tools hub, and the page three of the four tools sit on.
 *
 * Kagan, 2026-08-25, on where the Guts Check should go: *"buna güzel bir yer
 * bulup, ayrı buton koyalım, başka bir tool'un önünde olması garip olur."* So
 * the tools are peers on one hub, each with its own page and its own button —
 * not a funnel where one is the warm-up for another.
 *
 * The hub also brings the footer's Tools column back. It was deleted with a note
 * saying it would return when the tools existed. All four are ported now, so
 * it carries the hub and all four.
 *
 * THE FOURTH TOOL'S PAGE IS NOT IN THIS FILE. `toolsRoi` lives in its own
 * `pages/tools-roi-calculator.ts`, with its own route, per the convention PR
 * #187 settled — and this file is the reason that convention exists, since
 * three pages stacked in one module is three AI-editor runs appending to the
 * same region. The hub card below is the only thing the ROI calculator needs
 * from here. A fifth tool gets its own file too; do not add it here.
 */
export const tools: WrittenPage = {
  navLabel: 'Tools',
  title: 'Assessment tools',
  description:
    'Four short tools: how ready your organisation is for agentic AI, how far your ambition is from your execution, whether a transformation would survive contact with your own operating model, and what your technology budget could actually return.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Tools',
      headline: 'Find out where you actually are.',
      sub: 'Four tools, each built from what we see inside large organisations rather than from a maturity model somebody drew. Three of them score where you are; the fourth puts a number on what the technology you already pay for could return. None takes more than six minutes, and each one ends in something you can argue with.',
      ctas: [],
    },
    {
      kind: 'cards',
      heading: 'Pick the question you need answered',
      // Reversed 2026-09-01: back to a left-aligned, primary-coloured "Start
      // →" rather than the pill `buttonCtas` drew from 2026-08-31, matching
      // the home page's assessment band from earlier the same day. `ctaLarge`
      // steps the label up one size, since a plain link reads smaller than
      // the button it replaces.
      ctaLarge: true,
      // The title looks exactly as it did — the anchor inherits the heading's
      // colour and weight and draws no underline; only the cursor changes.
      linkTitles: true,
      items: [
        {
          kicker: '12 QUESTIONS · 4 MINUTES',
          title: 'The Guts Check',
          promise: 'Which of the four levels you are actually on, and how far your ambition sits from the weakest thing underneath it.',
          points: [
            'Ambition, ownership, execution and proof, scored separately',
            'The ambition–reality gap, which is the number that predicts a rescope',
            'Three things to do first, specific to your level',
          ],
          cta: { label: 'Start', href: '/en/tools/agentic-guts-check' },
        },
        {
          kicker: '18 QUESTIONS · 6 MINUTES',
          title: 'Agentic Readiness Index',
          promise: 'How much of your business you would actually let an agent run, scored on both sides of the question.',
          points: [
            'Six dimensions: what the business has authorised, and what the systems can do',
            'An archetype: ambition-led or capability-led',
            'The one gap that will stop you first',
          ],
          cta: {
            label: 'Start',
            href: '/en/tools/agentic-readiness-index',
          },
        },
        {
          kicker: '8 QUESTIONS · 3 MINUTES',
          title: 'Transformation Readiness Diagnostic',
          promise: 'Whether a transformation you are considering would survive its first difficult quarter.',
          points: [
            'Will and skill, measured as two separate axes',
            'Available in English and Turkish',
            'A thirty-minute conversation as the natural next step',
          ],
          cta: {
            label: 'Start',
            href: '/en/tools/transformation-readiness',
          },
        },
        {
          kicker: '4 INPUTS · 2 MINUTES',
          title: 'Technology ROI Calculator',
          promise: 'What your technology budget could return in a year, in the three parts a benefit case usually leaves out two of.',
          points: [
            'Your industry\u2019s IT spend and its Run / Grow / Transform split',
            'Tangible value, intangible value, and the cost of standing still',
            'Calibrated on real value cases, not on a vendor\u2019s multiple',
          ],
          cta: {
            label: 'Start',
            href: '/en/tools/roi-calculator',
          },
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Would rather just talk it through?',
      sub: 'Forty-five minutes with someone who has done this before, no deck.',
      ctas: [{ label: 'Book a conversation', href: '#contact' }],
      tone: 'band',
    },
  ],
}

export const toolsGutsCheck: WrittenPage = {
  navLabel: 'The Guts Check',
  title: 'The Guts Check: where are you on your agentic AI journey?',
  description:
    'Twelve questions on ambition, ownership, execution and proof. Find out which of the four levels your organisation is actually on, and how far your ambition is from your execution.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'The Guts Check',
      headline: 'Where are you on your agentic AI journey?',
      sub: 'Most organisations are further ahead on ambition than on the machinery that would make that ambition real. This measures both, and tells you the distance between them. That distance is the number that predicts whether a transformation gets delivered or quietly rescoped.',
      ctas: [],
    },
    { kind: 'tool', tool: 'agentic-guts-check' },
    {
      kind: 'prose',
      heading: 'What this is, and what it is not',
      paragraphs: [
        'It is four dimensions (ambition, decision and ownership, execution, and proof), each scored from what you say is true today rather than what is planned. The levels are the ones we see in practice: ignorance, efficiency, maturity, reinvention. Almost nobody is at the top, and plenty of well-run businesses are at the bottom for perfectly rational reasons.',
        'It is not a benchmark against your industry, and it does not tell you which vendor to buy. The most useful output is usually not the level but the gap: a Level 3 ambition sitting on Level 1 execution is the most reliable predictor of a programme that gets announced and then shrinks.',
      ],
    },
  ],
}

export const toolsDiagnostic: WrittenPage = {
  navLabel: 'Transformation Readiness',
  title: 'Transformation Readiness Diagnostic',
  description:
    'Eight questions on two axes: will and skill. Find out which of the four quadrants your organisation is in, and whether a transformation you are considering would survive its first difficult quarter.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'TRANSFORMATION READINESS DIAGNOSTIC',
      headline: 'Wanting it is not the same as being able to do it.',
      sub: 'Most transformations do not fail on strategy. They fail in the gap between the intent at the top and the capacity underneath it, and those two things are measured separately here, because averaging them hides exactly the problem you need to see.',
      ctas: [],
    },
    { kind: 'tool', tool: 'transformation-readiness' },
    {
      kind: 'prose',
      check:
        'THIS REPORT NAMES FOUR REAL COMPANIES, TWO OF THEM AS FAILURES. '
        + 'GE/Predix and Ford Smart Mobility are cited as cautionary precedents; '
        + 'DBS and John Deere as successes. All four are public, heavily '
        + 'documented stories and none of them is a Digitopia customer — but they '
        + 'are still named third parties on our own marketing site, shown to a '
        + 'reader we have just profiled. Halil and Kagan to confirm before launch '
        + 'that this is the intent, since it came across from the source tool '
        + 'rather than being a decision anybody took for the website.',
      paragraphs: [],
    },
    {
      kind: 'prose',
      heading: 'Why two axes and not one score',
      paragraphs: [
        'Will is intent that survives contact with a hard trade-off: a target state somebody could measure, leaders whose own mandate is at risk, a middle layer that reallocates people without being asked, and an explicit agreement about what to stop. Skill is the machinery: bench depth, decision rights, platforms and data a pilot can actually scale onto, and an organisation that surfaces failure in days rather than at the quarterly review.',
        'They are reported separately on purpose. High will with low skill and low will with high skill can produce the same average and are opposite problems: one is a credibility risk, the other is a capability sitting idle. The quadrant is the answer; the single number would be the thing that hid it.',
      ],
    },
  ],
}

export const toolsAri: WrittenPage = {
  navLabel: 'Agentic Readiness Index',
  title: 'Agentic Readiness Index',
  description:
    'Eighteen questions across six dimensions. How much of your business would you actually let an AI agent run, and can your systems deliver on what the business has already authorised?',
  sections: [
    {
      kind: 'hero',
      // Written in capitals rather than left to `text-transform: uppercase`.
      // These labels are set uppercase in CSS, and CSS casing follows the
      // document's language: under `lang="tr"` the browser maps `i` to `İ`, so
      // this English product name came out "AGENTİC READİNESS İNDEX" on the
      // Turkish page (asked about 2026-08-27). Turkish labels still transform
      // correctly and are left in sentence case — it is only English words and
      // brand names on a Turkish page that need the capitals spelled out.
      eyebrow: 'AGENTIC READINESS INDEX',
      headline: 'How much of your business would you actually let an agent run?',
      sub: 'Most organisations are further ahead on ambition than on the machinery that makes ambition safe. It scores both sides, what your business has authorised and what your systems are able to do, then names the gap between them.',
      ctas: [],
    },
    { kind: 'tool', tool: 'agentic-readiness-index' },
    {
      kind: 'prose',
      check:
        'THE REPORT ROUTES THE READER ONTO THREE NAMED DIGITOPIA '
        + 'offerings, off their three weakest dimensions. That mapping came from '
        + 'the source tool and nobody has reviewed it for the website — worth '
        + 'ten minutes with Halil, because a diagnosis that always ends in the '
        + 'same recommendation stops reading as a diagnosis.',
      paragraphs: [],
    },
    {
      kind: 'prose',
      heading: 'Hub and Core, and why both',
      paragraphs: [
        'Hub is what the business has authorised: whether there is a named unit of value with a baseline and an owner, how much decision-making you would actually delegate, and whether the organisation can absorb the change. Core is what the systems can do: whether an agent can reach governed knowledge, act inside your real systems of record, and be operated, monitored and proven once it is live.',
        'They are scored separately because the failure modes are opposite. High Hub and low Core is authorising more than you can safely deliver, which is the fastest route to an incident. Low Hub and high Core is a capability nobody has pointed at anything that matters. The index of the two is on the page, but it is the third number, not the first.',
      ],
    },
  ],
}
